import { DECISION, DECISION_USER } from 'business/constants';
import { useClientHasModule, useUserHasRole } from 'hooks/client';

import HeaderLayout from 'ui/HeaderLayout/HeaderLayout';
import ManageUserGroups from './UserGroups/ManageUserGroups';
import ManageUsers from './Users/ManageUsers';
import ModuleForbidden from 'components/ModuleAccess/RolesCheck/ModuleForbidden';
import NotFound from 'components/Errors/NotFound';
import { Paper } from '@mui/material';
import React from 'react';
import TabulatedPage from 'ui/TabulatedPage/TabulatedPage';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const StyUserManagementContainer = styled(Paper, {
    name: 'StyUserManagementContainer',
})({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    width: '100%',
});

const StyTabulatedPage = styled(TabulatedPage, {
    name: 'StyTabulatedPage',
})({
    // 117px corresponds to the height of the header + the height of the tabs section
    height: 'calc(100% - 117px)',
    '& .Mui-TabulatedPage-content-div': { height: '100%' },
});

const USER_MANAGEMENT_TABS = {
    users: 'users',
    userGroups: 'user_groups',
} as const;

const UserManagement = (): JSX.Element => {
    const { t } = useTranslation();
    const { tab } = useParams();
    const hasDecision = useClientHasModule(DECISION);
    const isDecisionUser = useUserHasRole(DECISION_USER);
    const canAccessUserGroupsPage = hasDecision && isDecisionUser;

    const tabsList = [
        {
            label: t('adminSpace.usersTab'),
            children: <ManageUsers />,
            pathname: `/admin/${USER_MANAGEMENT_TABS.users}`,
            disabled: false,
        },
        {
            label: t('adminSpace.userGroupsTab'),
            children: <ManageUserGroups />,
            pathname: `/admin/${USER_MANAGEMENT_TABS.userGroups}`,
            disabled: false,
        },
    ];

    if (!Object.values(USER_MANAGEMENT_TABS).includes(tab)) return <NotFound />;
    if (!canAccessUserGroupsPage && tab === USER_MANAGEMENT_TABS.userGroups)
        return (
            <ModuleForbidden
                title={t('moduleForbidden.title', {
                    module: t(`modules.${DECISION}`),
                })}
            />
        );

    return (
        <StyUserManagementContainer>
            <HeaderLayout title={t('adminSpace.headerTitle')} />
            {canAccessUserGroupsPage ? (
                <StyTabulatedPage tabsList={tabsList} variant="fullWidth" />
            ) : (
                <ManageUsers />
            )}
        </StyUserManagementContainer>
    );
};

export default UserManagement;
